import { z } from 'zod';

/**
 * KokoroWebSocketTTS — routes TTS through voice-service /ws/raw.
 * Plays Kokoro PCM16 audio via Web Audio API.
 * Singleton: one WS connection reused across speak() calls.
 */

function toWsUrl(url: string): string {
  return url.replace(/^http:\/\//, 'ws://').replace(/^https:\/\//, 'wss://');
}

const WS_BASE = toWsUrl(
  (import.meta.env.VITE_VOICE_SERVICE_URL as string | undefined) || 'ws://localhost:8007',
);

interface SpeakOptions {
  agentId?: string;
  onEnd?: () => void;
  onError?: () => void;
}

export class KokoroWebSocketTTS {
  private ws: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private nextPlayTime = 0;
  private token: string | null = null;
  private agentId: string | null = null;
  private connectPromise: Promise<void> | null = null;
  private activeOnEnd: (() => void) | null = null;
  private activeOnError: (() => void) | null = null;

  setToken(token: string | null): void { this.token = token; }
  setAgentId(id: string | null): void { this.agentId = id; }

  private buildUrl(): string {
    const agentParam = this.agentId ? `&agent_id=${encodeURIComponent(this.agentId)}` : '';
    return `${WS_BASE}/ws/raw?token=${encodeURIComponent(this.token || '')}${agentParam}`;
  }

  private async ensureConnected(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(this.buildUrl());
      const timeout = setTimeout(() => { ws.close(); reject(new Error('connect timeout')); }, 10000);

      ws.onopen = () => { clearTimeout(timeout); this.ws = ws; this.connectPromise = null; resolve(); };
      ws.onmessage = (e) => this.handleMessage(e.data as string);
      ws.onerror = () => {
        clearTimeout(timeout);
        this.connectPromise = null;
        reject(new Error('WebSocket error'));
        this.activeOnError?.();
      };
      ws.onclose = () => {
        this.ws = null;
        this.connectPromise = null;
        if (this.activeOnEnd) { this.activeOnEnd(); this.activeOnEnd = null; }
      };
    });
    return this.connectPromise;
  }

  async speak(text: string, options: SpeakOptions = {}): Promise<void> {
    // Ensure we have a fresh AudioContext for each utterance
    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      this.audioCtx = new AudioContext({ sampleRate: 24000 });
    }
    await this.audioCtx.resume();
    this.nextPlayTime = this.audioCtx.currentTime;
    this.activeOnEnd = options.onEnd ?? null;
    this.activeOnError = options.onError ?? null;

    try {
      await this.ensureConnected();
    } catch (e) {
      options.onError?.();
      throw e;
    }

    const msg: Record<string, string> = { type: 'speak', text };
    if (options.agentId) msg.agent_id = options.agentId;
    this.ws!.send(JSON.stringify(msg));
  }

  disconnect(): void {
    this.ws?.close();
    this.ws = null;
    this.audioCtx?.close().catch(() => {});
    this.audioCtx = null;
  }

  private handleMessage(data: string): void {
    const parsed = z.record(z.string(), z.unknown()).safeParse((() => { try { return JSON.parse(data); } catch { return undefined; } })());
    if (!parsed.success) return;
    const msg = parsed.data;

    if (msg.type === 'tts_audio' && typeof msg.data === 'string') {
      this.enqueueChunk(msg.data, typeof msg.sampleRate === 'number' ? msg.sampleRate : 24000);
    } else if (msg.type === 'tts_end') {
      // Schedule onEnd after the last queued buffer finishes playing
      const remaining = Math.max(0, this.nextPlayTime - (this.audioCtx?.currentTime ?? 0));
      setTimeout(() => {
        this.activeOnEnd?.();
        this.activeOnEnd = null;
      }, remaining * 1000 + 100);
    }
  }

  private enqueueChunk(base64: string, sampleRate: number): void {
    if (!this.audioCtx) return;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const int16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;

    const buffer = this.audioCtx.createBuffer(1, float32.length, sampleRate);
    buffer.getChannelData(0).set(float32);

    const source = this.audioCtx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioCtx.destination);

    const startAt = Math.max(this.nextPlayTime, this.audioCtx.currentTime);
    source.start(startAt);
    this.nextPlayTime = startAt + buffer.duration;
  }
}

export const kokoroTTS = new KokoroWebSocketTTS();
